import { Table } from "@devexpress/dx-react-grid-bootstrap4";
import { useCallback, useMemo, useState } from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { Link, useHistory } from "react-router-dom";
import { SortableHandle } from "react-sortable-hoc";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { useLocaleProvider } from "../../components/locale-provider";
import { useLoyaltyBrand } from "../../components/loyalty-brand-provider";
import { messages } from "../extras/messages";
import { StyledSwapArrow } from "../extras/styles";
import {
  DefaultTableRowActionsProps,
  Delete,
  Edit,
  StyledReactTooltip,
} from "../shared/actions/tableActions";
import { inputFilter } from "../shared/crud/filter-widgets";
import CrudList, { TableColumns, TableFilters } from "../shared/crud/list";
import { ArrayRenderer, IntegerRenderer, StringRenderer } from "../shared/renderers/table";
import { LoyaltyTagSectionTableDto } from "./infrastructure/dto/LoyaltyTagSectionTableDto";
import { useSections } from "./infrastructure/hooks/sections.hook";
import { LoyaltyTagSectionMapper } from "./infrastructure/mappers/LoyaltyTagSectionMapper";
import {
  loyaltyTagSectionsListMessages,
  loyaltyTagSectionsMessages,
} from "./LoyaltyTagSections.messages";

const columns: TableColumns<LoyaltyTagSectionTableDto> = {
  ticker: {
    renderer: StringRenderer,
  },
  name: {
    renderer: StringRenderer,
  },
  tags: {
    renderer: ArrayRenderer,
  },
  order: {
    renderer: IntegerRenderer,
  },
};

function TableActions() {
  return (
    <div className="flex gap-4">
      <Link className="btn btn-primary" to="loyalty-preferences/new">
        <FormattedMessage {...loyaltyTagSectionsMessages.createSection} />
      </Link>
      <Link className="btn btn-primary" to="loyalty-preferences/preferences">
        <FormattedMessage {...loyaltyTagSectionsMessages.preferencesManagement} />
      </Link>
    </div>
  );
}

function ILoyaltyTagSections() {
  const { currentLocale } = useLocaleProvider();
  const brandId = useLoyaltyBrand()?.externalSystemId || "";
  const history = useHistory();

  const [forceUpdate, setForceUpdate] = useState(0);
  const { getSections, deleteSection, reorderSection } = useSections(brandId);

  const handleGetAll = useCallback(async () => {
    const sections = await getSections();
    return sections
      .map(it => LoyaltyTagSectionMapper.toTableDto(it, currentLocale))
      .sort((a, b) => a.order - b.order);
  }, [currentLocale, getSections]);

  const handleOnEdit = useCallback((id: string) => history.push(`loyalty-preferences/edit/${id}`), [
    history,
  ]);
  const handleReload = useCallback(() => setForceUpdate(prev => prev + 1), []);
  const handleReorder = useCallback(
    async (id: string, after?: string) => {
      await reorderSection(id, after);
      handleReload();
    },
    [handleReload, reorderSection]
  );

  const RowActions = useMemo(
    () => ({ row, rest }: DefaultTableRowActionsProps<LoyaltyTagSectionTableDto>) => {
      const DragButton = SortableHandle(() => (
        <OverlayTrigger
          delay={{ show: 250, hide: 0 }}
          overlay={props => (
            <Tooltip id={`drag-tooltip-${row.id}`} {...props}>
              <FormattedMessage {...messages.drag} />
            </Tooltip>
          )}>
          <Button size="sm" className="mr-2" variant="light" style={{ cursor: "move" }}>
            <StyledSwapArrow />
          </Button>
        </OverlayTrigger>
      ));

      return (
        <Table.Cell {...rest} style={{ zIndex: 999 }}>
          <StyledReactTooltip id="edit" effect="solid">
            <FormattedMessage {...loyaltyTagSectionsMessages.edit} />
          </StyledReactTooltip>

          <Edit row={row} onGoToEdit={handleOnEdit} />
          <DragButton />
          <Delete
            rest={rest}
            row={row}
            onDelete={deleteSection}
            onReloadList={handleReload}
            useOverlay={true}
          />
        </Table.Cell>
      );
    },
    [deleteSection, handleOnEdit, handleReload]
  );

  const tableFilters: TableFilters<any, any> = useMemo(
    () => ({
      ticker: {
        filterFn: (ticker: string, filter: string) =>
          ticker.toLowerCase().includes(filter.toLowerCase()),
        widget: inputFilter,
      },
      name: {
        filterFn: (name: string, filter: string) =>
          name.toLowerCase().includes(filter.toLowerCase()),
        widget: inputFilter,
      },
      tags: {
        filterFn: (preferences: string[], filter: string) =>
          preferences.some(preference => preference.toLowerCase().includes(filter.toLowerCase())),
        widget: inputFilter,
      },
    }),
    []
  );

  if (!brandId) return null;

  return (
    <CrudList<LoyaltyTagSectionTableDto>
      columns={columns}
      actions={TableActions}
      filters={tableFilters}
      messages={loyaltyTagSectionsListMessages}
      onGetAll={handleGetAll}
      onReorder={handleReorder}
      rowActions={RowActions}
      forceLoad={forceUpdate}
      hideExportButton
    />
  );
}

export const LoyaltyTagSections = withAuthorizationVerifier(ILoyaltyTagSections);
